import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSupportSettings, updateSupportSettings } from '../../slice/supportSettingsSlice';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useTranslation } from '../../i18n/LanguageContext';
import { Pencil, X, Trash2, Plus, ChevronDown, Clock } from 'lucide-react';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_LABEL_KEYS = {
  monday: 'helpCenter.supportDayMonday',
  tuesday: 'helpCenter.supportDayTuesday',
  wednesday: 'helpCenter.supportDayWednesday',
  thursday: 'helpCenter.supportDayThursday',
  friday: 'helpCenter.supportDayFriday',
  saturday: 'helpCenter.supportDaySaturday',
  sunday: 'helpCenter.supportDaySunday',
};

const TIMEZONE_OPTIONS = [
  { code: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { code: 'ET',  label: 'Eastern Time (ET)' },
  { code: 'CT',  label: 'Central Time (CT)' },
  { code: 'MT',  label: 'Mountain Time (MT)' },
  { code: 'PT',  label: 'Pacific Time (PT)' },
  { code: 'GMT', label: 'London (GMT)' },
  { code: 'CET', label: 'Paris (CET)' },
  { code: 'JST', label: 'Tokyo (JST)' },
  { code: 'CST', label: 'Shanghai (CST)' },
  { code: 'AEST', label: 'Sydney (AEST)' },
];

const DATE_FORMAT_OPTIONS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];

const WEEK_START_OPTIONS = [
  'saturday',
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

// Format the *current* date according to a chosen pattern, used for the
// preview line under each Date Format card.
const formatToday = (pattern) => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  switch (pattern) {
    case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
    case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
    case 'MM/DD/YYYY':
    default:           return `${mm}/${dd}/${yyyy}`;
  }
};

const tzLabelByCode = (code) =>
  TIMEZONE_OPTIONS.find((tz) => tz.code === code)?.label || code || 'UTC';

// Format an "HH:MM" 24h string per user's chosen time format ('12' or '24').
// Returns the original string if it's malformed.
const formatTimeFor = (hhmm, format) => {
  if (!hhmm || typeof hhmm !== 'string') return hhmm || '--:--';
  const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (format === '24') {
    return `${String(h).padStart(2, '0')}:${min}`;
  }
  // 12-hour
  const period = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${min} ${period}`;
};

// Return the list of weekday keys reordered so it starts on `weekStart`.
// Falls back to Monday when the value is unknown.
const orderedDays = (weekStart) => {
  const start = DAY_KEYS.includes(weekStart) ? weekStart : 'monday';
  const idx = DAY_KEYS.indexOf(start);
  return [...DAY_KEYS.slice(idx), ...DAY_KEYS.slice(0, idx)];
};

const emptyHours = () =>
  DAY_KEYS.reduce((acc, day) => {
    acc[day] = { enabled: false, slots: [] };
    return acc;
  }, {});

const parseSupportHours = (raw) => {
  if (!raw) return emptyHours();
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const out = emptyHours();
    DAY_KEYS.forEach((day) => {
      const entry = parsed?.[day];
      if (entry && typeof entry === 'object') {
        out[day] = {
          enabled: !!entry.enabled,
          slots: Array.isArray(entry.slots)
            ? entry.slots
                .filter((s) => s && typeof s === 'object')
                .map((s) => ({ open: s.open || '07:00', close: s.close || '17:00' }))
            : [],
        };
      }
    });
    return out;
  } catch {
    return emptyHours();
  }
};

const Support = () => {
  const dispatch = useDispatch();
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const { data: userData } = useSelector((state) => state.admin);
  const { information_text, feedback_link, support_hours, loading, error } = useSelector(
    (state) => state.supportSettings
  );

  const [panelOpen, setPanelOpen] = useState(false);
  const [editInfoText, setEditInfoText] = useState('');
  const [editFeedbackLink, setEditFeedbackLink] = useState('');
  const [editHours, setEditHours] = useState(emptyHours());
  const [isSaving, setIsSaving] = useState(false);
  const maxCharacters = 500;

  // Time & Date Settings modal state
  const { timezone, date_format, time_format, week_starts_on } = useSelector(
    (state) => state.supportSettings
  );
  const [tdsModalOpen, setTdsModalOpen] = useState(false);
  const [editTimezone, setEditTimezone] = useState('UTC');
  const [editDateFormat, setEditDateFormat] = useState('MM/DD/YYYY');
  const [editTimeFormat, setEditTimeFormat] = useState('12');
  const [editWeekStartsOn, setEditWeekStartsOn] = useState('monday');
  const [tzDropdownOpen, setTzDropdownOpen] = useState(false);
  const [weekDropdownOpen, setWeekDropdownOpen] = useState(false);
  const [isSavingTds, setIsSavingTds] = useState(false);
  const tzDropdownRef = useRef(null);
  const weekDropdownRef = useRef(null);

  // Close TDS dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (tzDropdownRef.current && !tzDropdownRef.current.contains(e.target)) {
        setTzDropdownOpen(false);
      }
      if (weekDropdownRef.current && !weekDropdownRef.current.contains(e.target)) {
        setWeekDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isSuperAdmin = userData?.user_role === 'SUPER_ADMIN';
  const isAdmin = userData?.user_role === 'ADMIN';
  const isAdminRole = isSuperAdmin || isAdmin;

  useEffect(() => {
    dispatch(fetchSupportSettings());
  }, [dispatch]);

  useEffect(() => {
    setEditInfoText(information_text || '');
    setEditFeedbackLink(feedback_link || '');
    setEditHours(parseSupportHours(support_hours));
  }, [information_text, feedback_link, support_hours]);

  // Keep TDS modal's edit copies in sync with Redux state
  useEffect(() => {
    setEditTimezone(timezone || 'UTC');
    setEditDateFormat(date_format || 'MM/DD/YYYY');
    setEditTimeFormat(time_format || '12');
    setEditWeekStartsOn(week_starts_on || 'monday');
  }, [timezone, date_format, time_format, week_starts_on]);

  const openTdsModal = () => {
    setEditTimezone(timezone || 'UTC');
    setEditDateFormat(date_format || 'MM/DD/YYYY');
    setEditTimeFormat(time_format || '12');
    setEditWeekStartsOn(week_starts_on || 'monday');
    setTdsModalOpen(true);
  };

  const closeTdsModal = () => {
    setTzDropdownOpen(false);
    setWeekDropdownOpen(false);
    setTdsModalOpen(false);
  };

  const handleTdsSave = async () => {
    if (!isSuperAdmin) return;
    setIsSavingTds(true);
    try {
      // Send only the time/date fields plus the existing message/link/hours
      // (the API requires information_text + feedback_link as Form fields).
      await dispatch(
        updateSupportSettings({
          information_text: information_text || '',
          feedback_link: feedback_link || '',
          support_hours: support_hours || JSON.stringify(emptyHours()),
          timezone: editTimezone,
          date_format: editDateFormat,
          time_format: editTimeFormat,
          week_starts_on: editWeekStartsOn,
        })
      ).unwrap();
      closeTdsModal();
    } catch (err) {
      console.error('Failed to update time & date settings:', err);
    } finally {
      setIsSavingTds(false);
    }
  };

  const openPanel = () => {
    setEditInfoText(information_text || '');
    setEditFeedbackLink(feedback_link || '');
    setEditHours(parseSupportHours(support_hours));
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
  };

  const handleSave = async () => {
    if (!isSuperAdmin) return;
    setIsSaving(true);
    try {
      await dispatch(
        updateSupportSettings({
          information_text: editInfoText,
          feedback_link: editFeedbackLink,
          support_hours: JSON.stringify(editHours),
        })
      ).unwrap();
      // The slice writes the new values into state optimistically on
      // fulfilled, so the page reflects the changes without re-fetching.
      closePanel();
    } catch (err) {
      console.error('Failed to update support settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDay = (day) => {
    setEditHours((prev) => {
      const cur = prev[day] || { enabled: false, slots: [] };
      const enabled = !cur.enabled;
      return {
        ...prev,
        [day]: {
          enabled,
          slots: enabled && cur.slots.length === 0 ? [{ open: '07:00', close: '17:00' }] : cur.slots,
        },
      };
    });
  };

  const addSlot = (day) => {
    setEditHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: [...(prev[day]?.slots || []), { open: '07:00', close: '17:00' }],
      },
    }));
  };

  const removeSlot = (day, index) => {
    setEditHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: (prev[day]?.slots || []).filter((_, i) => i !== index),
      },
    }));
  };

  const updateSlot = (day, index, field, value) => {
    setEditHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: (prev[day]?.slots || []).map((s, i) => (i === index ? { ...s, [field]: value } : s)),
      },
    }));
  };

  // Display: build a summary line per day for the public card.
  // Honors the user's selected time format (12h / 24h) and week start day.
  const displayHours = parseSupportHours(support_hours);
  const displayDayOrder = orderedDays(week_starts_on);
  const formatSlots = (slots) =>
    (slots || [])
      .map(
        (s) =>
          `${formatTimeFor(s.open, time_format)} – ${formatTimeFor(s.close, time_format)}`
      )
      .join(', ');

  // Support icon component
  const SupportIcon = () => (
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${aispaceColor}15` }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16V12C11 11.45 11.45 11 12 11C12.55 11 13 11.45 13 12V16C13 16.55 12.55 17 12 17ZM13 9H11V7H13V9Z"
          fill={aispaceColor}
        />
      </svg>
    </div>
  );

  // Toggle switch component
  const Toggle = ({ checked, onChange, disabled }) => (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      style={{ backgroundColor: checked ? aispaceColor : '#D1D5DB' }}
      aria-pressed={checked}
    >
      <span
        className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? 'translateX(22px)' : 'translateX(2px)' }}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: aispaceColor }}
        ></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Support Information Card */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        {/* Card Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#F3F4F6]">
          <div className="flex items-center gap-4">
            <SupportIcon />
            <div>
              <h3 className="text-[16px] font-semibold text-[#111827]">{t('helpCenter.supportInfoTitle')}</h3>
              <p className="text-[14px] text-[#6B7280]">{t('helpCenter.supportInfoSubtitle')}</p>
            </div>
          </div>
          {isSuperAdmin && (
            <button
              onClick={openPanel}
              className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors flex-shrink-0"
            >
              <Pencil size={16} />
              {t('common.edit')}
            </button>
          )}
        </div>

        {/* Card Content */}
        <div className="py-6 space-y-6">
          {/* Support Message */}
          <div className="pl-[72px] pr-6">
            <h4 className="text-[14px] font-semibold text-[#111827] mb-1">{t('helpCenter.supportMessageLabel')}</h4>
            <p className="text-[14px] text-[#6B7280]">
              {information_text || t('helpCenter.supportMessageDefault')}
            </p>
          </div>

          {/* Support Request Link */}
          <div className="pl-[72px] pr-6">
            <h4 className="text-[14px] font-semibold text-[#111827] mb-1">{t('helpCenter.supportRequestLinkLabel')}</h4>
            <a
              href={feedback_link || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[14px] hover:underline"
              style={{ color: aispaceColor }}
            >
              {feedback_link || t('helpCenter.supportNoLinkConfigured')}
            </a>
          </div>

          {/* Support Hours per day */}
          <div className="pl-[72px] pr-6">
            <h4 className="text-[14px] font-semibold text-[#111827] mb-2">{t('helpCenter.supportHoursLabel')}</h4>
            <div className="space-y-1.5 max-w-[768px]">
              {displayDayOrder.map((day) => {
                const entry = displayHours[day];
                const open = entry?.enabled && entry.slots.length > 0;
                return (
                  <div key={day} className="flex items-center justify-between text-[14px]">
                    <span className="text-[#374151] font-medium">{t(DAY_LABEL_KEYS[day])}</span>
                    <span className="text-[#6B7280]">
                      {open ? formatSlots(entry.slots) : t('helpCenter.supportClosed')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Time & Date Settings Card — only visible to SUPER_ADMIN */}
      {isSuperAdmin && <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        {/* Card Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#F3F4F6]">
          <div className="flex items-center gap-4">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${aispaceColor}15` }}
            >
              <Clock size={20} style={{ color: aispaceColor }} />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-[#111827]">
                {t('helpCenter.tdsCardTitle')}
              </h3>
              <p className="text-[14px] text-[#6B7280]">
                {t('helpCenter.tdsCardSubtitle')}
              </p>
            </div>
          </div>
          {isSuperAdmin && (
            <button
              onClick={openTdsModal}
              className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors flex-shrink-0"
            >
              <Pencil size={16} />
              {t('common.edit')}
            </button>
          )}
        </div>

        {/* Card Content */}
        <div className="py-6 space-y-5 pl-[72px] pr-6">
          <div>
            <h4 className="text-[14px] font-semibold text-[#111827] mb-1">
              {t('helpCenter.tdsTimezoneLabel')}
            </h4>
            <p className="text-[14px] text-[#6B7280]">{tzLabelByCode(timezone)}</p>
          </div>
          <div>
            <h4 className="text-[14px] font-semibold text-[#111827] mb-1">
              {t('helpCenter.tdsDateFormatLabel')}
            </h4>
            <p className="text-[14px] text-[#374151]">{date_format || 'MM/DD/YYYY'}</p>
            <p className="text-[13px] text-[#6B7280] mt-0.5">{formatToday(date_format || 'MM/DD/YYYY')}</p>
          </div>
          <div>
            <h4 className="text-[14px] font-semibold text-[#111827] mb-1">
              {t('helpCenter.tdsTimeFormatLabel')}
            </h4>
            <p className="text-[14px] text-[#374151]">
              {time_format === '24' ? t('helpCenter.tdsTime24Label') : t('helpCenter.tdsTime12Label')}
            </p>
            <p className="text-[13px] text-[#6B7280] mt-0.5">
              {time_format === '24'
                ? t('helpCenter.tdsTime24Example')
                : t('helpCenter.tdsTime12Example')}
            </p>
          </div>
          <div>
            <h4 className="text-[14px] font-semibold text-[#111827] mb-1">
              {t('helpCenter.tdsWeekStartsOnLabel')}
            </h4>
            <p className="text-[14px] text-[#6B7280]">
              {t(DAY_LABEL_KEYS[week_starts_on] || DAY_LABEL_KEYS.monday)}
            </p>
          </div>
        </div>
      </div>}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-[14px]">
          {t('helpCenter.supportErrorPrefix', { message: error })}
        </div>
      )}

      {/* Slide-in Panel */}
      {panelOpen && (
        <div className="fixed inset-0 z-[200]" style={{ margin: 0, padding: 0 }}>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 transition-opacity"
            onClick={isSaving ? undefined : closePanel}
            style={{ margin: 0, padding: 0 }}
          />

          {/* Panel */}
          <div className="fixed top-0 right-0 h-screen w-[520px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <h2 className="text-[18px] font-semibold text-[#111827]">{t('helpCenter.supportInfoTitle')}</h2>
              <button
                onClick={closePanel}
                disabled={isSaving}
                className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-6">
                {/* Support Message */}
                <div>
                  <label className="block text-[14px] font-medium text-[#111827] mb-2">
                    {t('helpCenter.supportMessageLabel')}
                  </label>
                  <textarea
                    value={editInfoText}
                    onChange={(e) => {
                      if (e.target.value.length <= maxCharacters) {
                        setEditInfoText(e.target.value);
                      }
                    }}
                    placeholder={t('helpCenter.supportMessagePlaceholder')}
                    className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0 resize-none"
                    onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
                    onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                    rows={5}
                  />
                  <p className="text-[13px] text-[#9CA3AF] mt-1">
                    {t('helpCenter.supportCharactersRemaining', { count: maxCharacters - editInfoText.length })}
                  </p>
                </div>

                {/* Support Request Link */}
                <div>
                  <label className="block text-[14px] font-medium text-[#111827] mb-2">
                    {t('helpCenter.supportRequestLinkLabel')}
                  </label>
                  <input
                    type="url"
                    value={editFeedbackLink}
                    onChange={(e) => setEditFeedbackLink(e.target.value)}
                    placeholder={t('helpCenter.supportLinkPlaceholder')}
                    className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0"
                    onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
                    onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                  />
                </div>

                {/* Support Hours - Per day */}
                <div>
                  <label className="block text-[14px] font-medium text-[#111827] mb-3">
                    {t('helpCenter.supportHoursLabel')}
                  </label>
                  <div className="space-y-3">
                    {orderedDays(week_starts_on).map((day) => {
                      const entry = editHours[day] || { enabled: false, slots: [] };
                      return (
                        <div
                          key={day}
                          className="border border-[#E5E7EB] rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-[14px] font-semibold text-[#111827]">
                                {t(DAY_LABEL_KEYS[day])}
                              </p>
                              {!entry.enabled && (
                                <p className="text-[13px] text-[#6B7280] mt-0.5">
                                  {t('helpCenter.supportClosed')}
                                </p>
                              )}
                            </div>
                            <Toggle checked={!!entry.enabled} onChange={() => toggleDay(day)} />
                          </div>

                          {entry.enabled && (
                            <div className="mt-3 space-y-2">
                              {entry.slots.map((slot, idx) => (
                                <div key={idx} className="flex items-end gap-2">
                                  <div className="flex-1">
                                    <label className="block text-[12px] text-[#6B7280] mb-1">
                                      {t('helpCenter.supportOpenLabel')}
                                    </label>
                                    <input
                                      type="time"
                                      value={slot.open || ''}
                                      onChange={(e) => updateSlot(day, idx, 'open', e.target.value)}
                                      className="w-full px-3 py-2 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2"
                                      style={{ '--tw-ring-color': aispaceColor }}
                                    />
                                  </div>
                                  <span className="pb-2 text-[#9CA3AF]">–</span>
                                  <div className="flex-1">
                                    <label className="block text-[12px] text-[#6B7280] mb-1">
                                      {t('helpCenter.supportCloseLabel')}
                                    </label>
                                    <input
                                      type="time"
                                      value={slot.close || ''}
                                      onChange={(e) => updateSlot(day, idx, 'close', e.target.value)}
                                      className="w-full px-3 py-2 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2"
                                      style={{ '--tw-ring-color': aispaceColor }}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeSlot(day, idx)}
                                    aria-label={t('helpCenter.supportRemoveHours')}
                                    className="p-2 text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition-colors mb-0"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addSlot(day)}
                                className="flex items-center gap-1.5 text-[13px] font-medium hover:opacity-80 transition-opacity"
                                style={{ color: aispaceColor }}
                              >
                                <Plus size={14} />
                                {t('helpCenter.supportAddHours')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 px-6 py-4 border-t border-[#E5E7EB]">
              <button
                onClick={closePanel}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: aispaceColor }}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('helpCenter.supportSaving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time & Date Settings Modal — only for SUPER_ADMIN */}
      {isSuperAdmin && tdsModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30"
            onClick={isSavingTds ? undefined : closeTdsModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[640px] flex flex-col max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <h2 className="text-[20px] font-semibold text-[#111827]">
                {t('helpCenter.tdsModalTitle')}
              </h2>
              <button
                onClick={closeTdsModal}
                disabled={isSavingTds}
                className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">
              {/* Timezone */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                  {t('helpCenter.tdsTimezoneLabel')}
                </label>
                <div className="relative" ref={tzDropdownRef}>
                  <button
                    type="button"
                    onClick={() => { setTzDropdownOpen(o => !o); setWeekDropdownOpen(false); }}
                    className="w-full flex items-center justify-between px-4 py-3 text-[14px] text-left border rounded-lg bg-white focus:outline-none transition-colors"
                    style={{ borderColor: tzDropdownOpen ? aispaceColor : '#E5E7EB' }}
                  >
                    <span className="text-[#374151]">{tzLabelByCode(editTimezone)}</span>
                    <ChevronDown
                      size={18}
                      className={`text-[#6B7280] transition-transform ${tzDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {tzDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[280px] overflow-y-auto">
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <div
                          key={tz.code}
                          onClick={() => { setEditTimezone(tz.code); setTzDropdownOpen(false); }}
                          className="px-4 py-2.5 text-[14px] cursor-pointer transition-colors hover:bg-[#F9FAFB]"
                          style={editTimezone === tz.code ? { backgroundColor: `${aispaceColor}10`, color: aispaceColor, fontWeight: 500 } : { color: '#374151' }}
                        >
                          {tz.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Date Format */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                  {t('helpCenter.tdsDateFormatLabel')}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {DATE_FORMAT_OPTIONS.map((fmt) => {
                    const selected = editDateFormat === fmt;
                    return (
                      <button
                        key={fmt}
                        type="button"
                        onClick={() => setEditDateFormat(fmt)}
                        className="flex flex-col items-start text-left p-4 border-2 rounded-xl transition-all"
                        style={
                          selected
                            ? { borderColor: aispaceColor, backgroundColor: `${aispaceColor}10`, color: aispaceColor }
                            : { borderColor: '#E5E7EB', color: '#111827' }
                        }
                      >
                        <span className={`text-[15px] font-semibold ${selected ? '' : 'text-[#111827]'}`}>{fmt}</span>
                        <span className="text-[13px] mt-1" style={{ color: selected ? aispaceColor : '#6B7280' }}>
                          {formatToday(fmt)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time Format */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                  {t('helpCenter.tdsTimeFormatLabel')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { code: '12', label: t('helpCenter.tdsTime12Label'), example: t('helpCenter.tdsTime12Example') },
                    { code: '24', label: t('helpCenter.tdsTime24Label'), example: t('helpCenter.tdsTime24Example') },
                  ].map((opt) => {
                    const selected = editTimeFormat === opt.code;
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => setEditTimeFormat(opt.code)}
                        className="flex flex-col items-start text-left p-4 border-2 rounded-xl transition-all"
                        style={
                          selected
                            ? { borderColor: aispaceColor, backgroundColor: `${aispaceColor}10`, color: aispaceColor }
                            : { borderColor: '#E5E7EB', color: '#111827' }
                        }
                      >
                        <span className={`text-[15px] font-semibold ${selected ? '' : 'text-[#111827]'}`}>{opt.label}</span>
                        <span className="text-[13px] mt-1" style={{ color: selected ? aispaceColor : '#6B7280' }}>
                          {opt.example}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Week Starts On */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                  {t('helpCenter.tdsWeekStartsOnLabel')}
                </label>
                <div className="relative" ref={weekDropdownRef}>
                  <button
                    type="button"
                    onClick={() => { setWeekDropdownOpen(o => !o); setTzDropdownOpen(false); }}
                    className="w-full flex items-center justify-between px-4 py-3 text-[14px] text-left border rounded-lg bg-white focus:outline-none transition-colors"
                    style={{ borderColor: weekDropdownOpen ? aispaceColor : '#E5E7EB' }}
                  >
                    <span className="text-[#374151]">{t(DAY_LABEL_KEYS[editWeekStartsOn] || DAY_LABEL_KEYS.monday)}</span>
                    <ChevronDown
                      size={18}
                      className={`text-[#6B7280] transition-transform ${weekDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {weekDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg overflow-hidden">
                      {WEEK_START_OPTIONS.map((day) => (
                        <div
                          key={day}
                          onClick={() => { setEditWeekStartsOn(day); setWeekDropdownOpen(false); }}
                          className="px-4 py-2.5 text-[14px] cursor-pointer transition-colors hover:bg-[#F9FAFB]"
                          style={editWeekStartsOn === day ? { backgroundColor: `${aispaceColor}10`, color: aispaceColor, fontWeight: 500 } : { color: '#374151' }}
                        >
                          {t(DAY_LABEL_KEYS[day])}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
              <button
                onClick={closeTdsModal}
                disabled={isSavingTds}
                className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleTdsSave}
                disabled={isSavingTds}
                className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 flex items-center gap-2 min-w-[100px] justify-center"
                style={{ backgroundColor: aispaceColor }}
              >
                {isSavingTds ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('helpCenter.tdsSaving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Support;
